import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { QuantityDialogModel } from './quantity-dialog.model';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-quantity-dialog',
  templateUrl: './quantity-dialog.component.html',
  styleUrls: ['./quantity-dialog.component.scss'],
  standalone: false,
})
export class QuantityDialogComponent {
  public quantityForm = new UntypedFormGroup({
    whole: new UntypedFormControl(this.splitDouble(this.model.quantity)[0], [
      Validators.min(0),
      Validators.pattern(/^\d+$/),
      Validators.required,
    ]),
    decimal: new UntypedFormControl(this.splitDouble(this.model.quantity)[1], [
      Validators.min(0),
      Validators.max(99),
      Validators.pattern(/^[0-9]{0,2}$/),
    ]),
  });

  constructor(
    public dialogRef: MatDialogRef<QuantityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public model: QuantityDialogModel,
  ) {
    this.setInitialValues();
  }

  canSubmit() {
    const wholeHasErrors =
      this.getWhole().hasError('max') ||
      this.getWhole().hasError('min') ||
      this.getWhole().hasError('pattern') ||
      this.getWhole().hasError('required');

    const decimalHasErrors =
      this.getDecimal().hasError('max') || this.getDecimal().hasError('min') || this.getDecimal().hasError('pattern');

    const formHasErrors = this.quantityForm.errors?.greaterThanTotal;

    return wholeHasErrors || decimalHasErrors || formHasErrors;
  }

  getWhole() {
    return this.quantityForm.get('whole');
  }

  setWhole(value: number | string) {
    this.quantityForm.get('whole').setValue(typeof value === 'string' ? value : String(value));
  }

  getDecimal() {
    return this.quantityForm.get('decimal');
  }

  setDecimal(value: number | string) {
    this.quantityForm.get('decimal').setValue(typeof value === 'string' ? value : String(value));
  }

  splitDouble(double: number | null): number[] {
    return double !== null
      ? String(double)
          .split(/[,.]/)
          .map((value) => Number(value))
      : [0, 0];
  }

  combineToDouble(whole: number, decimal: number) {
    return Number(`${whole}.${decimal ? decimal : 0}`);
  }

  setInitialValues() {
    const breakdown = this.splitDouble(this.model.quantity);
    this.setWhole(breakdown[0]);
    this.setDecimal(breakdown[1] ? breakdown[1] : '00');
  }

  increaseValueBy(increase: number) {
    const whole = Number(this.getWhole().value);
    this.setWhole(whole + increase);
    if (whole === this.model.total) {
      this.setDecimal('00');
    }
  }

  setValuesToTotal() {
    const breakdown = this.splitDouble(this.model.total);
    this.setWhole(breakdown[0]);
    this.setDecimal(breakdown[1] ? breakdown[1] : '00');
  }

  confirm(): void {
    this.model.quantity = this.combineToDouble(this.getWhole().value, this.getDecimal().value);
    this.dialogRef.close(this.model);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
